import React, { useEffect, useState } from "react";
import styles from "./Subscription.module.css";
import Button from "../../component/button/Button";
import InputText from "../../component/inputText/InputText";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  createSubscriptionPlan,
  updateSubscriptionPlan,
} from "../../api/HttpApis";

const CYCLES = ["month", "year"];
const QUALITIES = ["SD", "HD", "FHD", "4K"];
const ACCESS_LEVELS = [
  { value: "limited", label: "Limited" },
  { value: "most", label: "Most content" },
  { value: "full", label: "Full access" },
];

const emptyForm = {
  name: "",
  tagline: "",
  description: "",
  features: "",
  content_access: "limited",
  max_devices: 1,
  max_profiles: 1,
  video_quality: "HD",
  ad_pre_roll: true,
  ad_mid_roll: true,
  ad_post_roll: true,
  sort_order: 1,
  status: 1,
  prices: [
    {
      billing_cycle: "month",
      billing_interval: 1,
      price: "",
      currency: "USD",
      trial_days: 0,
      stripe_payment_link: "",
      is_default: true,
      roku_product_id: "",
      firetv_product_id: "",
      android_product_id: "",
      tvos_product_id: "",
    },
  ],
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "var(--app-secondaryBg)",
  color: "var(--text-secondary)",
  padding: "2rem",
  borderRadius: "12px",
  width: "min(820px, 92vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  border: "1px solid var(--border-color)",
};

const InfoIcon = ({ text }) => (
  <span className={styles["info-icon"]} tabIndex={0} aria-label={text}>
    i
    <span className={styles["info-tooltip"]}>{text}</span>
  </span>
);

const FieldLabel = ({ children, info, required }) => (
  <div className={styles["label-row"]}>
    <span>
      {children}
      {required && " *"}
    </span>
    {info && <InfoIcon text={info} />}
  </div>
);

const ErrText = ({ msg }) =>
  msg ? <small className={styles["field-error"]}>{msg}</small> : null;

const PlanModal = ({ editData, onClose, totalPlans = 0 }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Sort-order ceiling: how many positions are valid.
  //   - Editing  : sort_order can be 1..N (N = existing total, since the plan
  //                is already counted).
  //   - Creating : sort_order can be 1..N+1 (we're adding one more, so the
  //                new plan can land at the end).
  const maxSortOrder = editData ? Math.max(1, totalPlans) : Math.max(1, totalPlans + 1);

  useEffect(() => {
    if (editData) {
      const ad = editData.ad_config || {};
      setFormData({
        name: editData.name || "",
        tagline: editData.tagline || "",
        description: editData.description || "",
        features: Array.isArray(editData.features)
          ? editData.features.join("\n")
          : "",
        content_access: editData.content_access || "limited",
        max_devices: editData.max_devices || 1,
        max_profiles: editData.max_profiles || 1,
        video_quality: editData.video_quality || "HD",
        ad_pre_roll: ad.pre_roll !== undefined ? !!ad.pre_roll : true,
        ad_mid_roll: ad.mid_roll !== undefined ? !!ad.mid_roll : true,
        ad_post_roll: ad.post_roll !== undefined ? !!ad.post_roll : true,
        sort_order: editData.sort_order || 1,
        status:
          editData.status === "Active"
            ? 1
            : editData.status === "Inactive"
            ? 0
            : 1,
        prices:
          Array.isArray(editData.prices) && editData.prices.length > 0
            ? editData.prices.map((p) => ({
                billing_cycle: p.billing_cycle,
                billing_interval: p.billing_interval || 1,
                price: p.price,
                currency: p.currency || "USD",
                trial_days: p.trial_days || 0,
                stripe_payment_link: p.stripe_payment_link || "",
                is_default: !!p.is_default,
                roku_product_id: p.roku_product_id || "",
                firetv_product_id: p.firetv_product_id || "",
                android_product_id: p.android_product_id || "",
                tvos_product_id: p.tvos_product_id || "",
              }))
            : emptyForm.prices,
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
  }, [editData]);

  const setField = (field, value) => {
    // sort_order: clamp to [1, maxSortOrder]. Empty string is left alone so
    // the user can clear-and-retype without the field jumping back to "1".
    if (field === "sort_order" && value !== "") {
      const n = parseInt(value, 10);
      value = Number.isNaN(n) ? "" : Math.min(maxSortOrder, Math.max(1, n));
    }
    setFormData((f) => ({ ...f, [field]: value }));
  };

  const setPriceField = (idx, field, value) => {
    // trial_days: never negative. 0 = no trial, valid lower bound.
    if (field === "trial_days" && value !== "") {
      const n = parseInt(value, 10);
      value = Number.isNaN(n) ? "" : Math.max(0, n);
    }
    // price (amount): never negative. min="0" on the input only guards arrow
    // buttons — typing, pasting, or arrow-keying past 0 still goes through
    // without this clamp. Keep as a decimal (Number, not parseInt) so $4.99
    // doesn't get coerced to 4.
    if (field === "price" && value !== "") {
      const n = Number(value);
      if (!Number.isNaN(n) && n < 0) value = "0";
    }
    // billing_interval: never negative, never zero.
    if (field === "billing_interval" && value !== "") {
      const n = parseInt(value, 10);
      value = Number.isNaN(n) ? "" : Math.max(1, n);
    }
    setFormData((f) => {
      const next = f.prices.map((p, i) =>
        i === idx ? { ...p, [field]: value } : p
      );
      return { ...f, prices: next };
    });
  };

  const setPriceDefault = (idx) => {
    setFormData((f) => ({
      ...f,
      prices: f.prices.map((p, i) => ({ ...p, is_default: i === idx })),
    }));
  };

  const addPriceRow = () => {
    setFormData((f) => {
      // Cap: one row per billing cycle. We support `month` and `year`, so
      // max 2 rows. Beyond that you'd be adding a duplicate cycle, which
      // makes no sense (Stripe doesn't allow two monthly prices on one plan
      // anyway). The button itself is also disabled at this length.
      if (f.prices.length >= CYCLES.length) return f;
      // Default the new row to the first cycle that isn't already used,
      // so a "Add Price" click on a plan with `month` jumps straight to `year`.
      const used = new Set(f.prices.map((p) => p.billing_cycle));
      const nextCycle = CYCLES.find((c) => !used.has(c)) || CYCLES[0];
      return {
        ...f,
        prices: [
          ...f.prices,
          {
            billing_cycle: nextCycle,
            billing_interval: 1,
            price: "",
            currency: "USD",
            trial_days: 0,
            stripe_payment_link: "",
            is_default: false,
            roku_product_id: "",
            firetv_product_id: "",
            android_product_id: "",
            tvos_product_id: "",
          },
        ],
      };
    });
  };

  const removePriceRow = (idx) => {
    setFormData((f) => {
      if (f.prices.length <= 1) return f;
      const next = f.prices.filter((_, i) => i !== idx);
      if (!next.some((p) => p.is_default)) next[0].is_default = true;
      return { ...f, prices: next };
    });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose(false);
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Name is required";
    if (!formData.description.trim()) e.description = "Description is required";

    formData.prices.forEach((p, i) => {
      if (p.price === "" || p.price === null || isNaN(parseFloat(p.price)) || parseFloat(p.price) < 0) {
        e[`price_${i}`] = "Price must be 0 or greater";
      }
      if (!CYCLES.includes(p.billing_cycle)) {
        e[`cycle_${i}`] = "Invalid billing cycle";
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    const payload = {
      name: formData.name,
      tagline: formData.tagline || null,
      description: formData.description,
      features: formData.features
        .split("\n")
        .map((f) => f.trim())
        .filter(Boolean),
      content_access: formData.content_access,
      max_devices: parseInt(formData.max_devices) || 1,
      max_profiles: parseInt(formData.max_profiles) || 1,
      video_quality: formData.video_quality,
      ad_pre_roll: !!formData.ad_pre_roll,
      ad_mid_roll: !!formData.ad_mid_roll,
      ad_post_roll: !!formData.ad_post_roll,
      sort_order: Math.max(1, parseInt(formData.sort_order) || 1),
      status: parseInt(formData.status),
      prices: formData.prices.map((p) => ({
        billing_cycle: p.billing_cycle,
        billing_interval: parseInt(p.billing_interval) || 1,
        price: parseFloat(p.price),
        currency: (p.currency || "USD").toUpperCase(),
        trial_days: parseInt(p.trial_days) || 0,
        stripe_payment_link:
          typeof p.stripe_payment_link === "string" &&
          p.stripe_payment_link.trim()
            ? p.stripe_payment_link.trim()
            : null,
        is_default: !!p.is_default,
        roku_product_id:
          typeof p.roku_product_id === "string" && p.roku_product_id.trim()
            ? p.roku_product_id.trim()
            : null,
        firetv_product_id:
          typeof p.firetv_product_id === "string" && p.firetv_product_id.trim()
            ? p.firetv_product_id.trim()
            : null,
        android_product_id:
          typeof p.android_product_id === "string" && p.android_product_id.trim()
            ? p.android_product_id.trim()
            : null,
        tvos_product_id:
          typeof p.tvos_product_id === "string" && p.tvos_product_id.trim()
            ? p.tvos_product_id.trim()
            : null,
      })),
    };

    setLoading(true);
    try {
      let response;
      if (editData) {
        response = await updateSubscriptionPlan(editData.id, payload);
        showToast(response?.message || "Plan updated", "success");
      } else {
        response = await createSubscriptionPlan(payload);
        showToast(response?.message || "Plan created", "success");
      }
      onClose(true);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const flat = {};
        Object.entries(apiErrors).forEach(([k, v]) => {
          flat[k] = Array.isArray(v) ? v[0] : v;
        });
        setErrors((prev) => ({ ...prev, ...flat }));
      }
      showToast(
        err?.response?.data?.message || "Failed to save plan",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={handleOverlayClick}>
      <div style={modalStyle}>
        <h2 style={{ marginTop: 0, color: "var(--text-dark)" }}>
          {editData ? "Edit Plan" : "Add New Plan"}
        </h2>

        <form onSubmit={handleSubmit} className={styles["api-form"]}>
          {/* General */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className={styles["form-group"]}>
              <FieldLabel
                required
                info="Display name shown to users (e.g. Premium, Basic, Free)"
              >
                Name
              </FieldLabel>
              <InputText
                name="name"
                maxLength={40}
                value={formData.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Premium"
                className={styles["input-dark"]}
              />
              <ErrText msg={errors.name} />
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel
                info={`Lower numbers show first when listing plans. Valid range: 1–${maxSortOrder}`}
              >
                Sort Order (1–{maxSortOrder})
              </FieldLabel>
              <InputText
                name="sort_order"
                type="number"
                min="1"
                max={maxSortOrder}
                value={formData.sort_order}
                onChange={(e) => setField("sort_order", e.target.value)}
                className={styles["input-dark"]}
              />
            </div>
          </div>

          <div className={styles["form-group"]}>
            <FieldLabel info="Short slogan shown under the plan name on the pricing page">
              Tagline
            </FieldLabel>
            <InputText
              name="tagline"
              maxLength={80}
              value={formData.tagline}
              onChange={(e) => setField("tagline", e.target.value)}
              placeholder="Great value. Great content."
              className={styles["input-dark"]}
            />
          </div>

          <div className={styles["form-group"]}>
            <FieldLabel
              required
              info="One-line description shown beneath the tagline"
            >
              Description
            </FieldLabel>
            <textarea
              name="description"
              maxLength={200}
              value={formData.description}
              onChange={(e) => setField("description", e.target.value)}
              className={styles["plan-field-textarea"]}
              rows={2}
              placeholder="Short description shown to users"
            />
            <ErrText msg={errors.description} />
          </div>

          {/* Prices */}
          <div className={styles["prices-editor"]}>
            <div className={styles["prices-editor-head"]}>
              <FieldLabel info='Add one row per billing cycle. For monthly+yearly, add two rows (e.g. cycle "month" $4.99 and cycle "year" $49.99). Mark one as Default — that price is preselected on the pricing page.'>
                Prices
              </FieldLabel>
              <Button
                variant="outline"
                type="button"
                onClick={addPriceRow}
                disabled={formData.prices.length >= CYCLES.length}
                title={
                  formData.prices.length >= CYCLES.length
                    ? `Maximum ${CYCLES.length} prices reached (one per billing cycle)`
                    : "Add a billing cycle (month or year)"
                }
              >
                + Add Price
              </Button>
            </div>

            {formData.prices.length === 0 ? (
              <div className={styles["prices-empty"]}>
                No prices yet — click "+ Add Price"
              </div>
            ) : (
              <>
                <div className={styles["prices-row-headers"]}>
                  <span>Cycle</span>
                  <span>Every</span>
                  <span>Amount</span>
                  <span>Currency</span>
                  <span>Trial (days)</span>
                  <span>Default</span>
                  <span>{/* remove btn col */}</span>
                </div>
                {formData.prices.map((p, idx) => (
                  <div key={idx} style={{ marginBottom: "0.75rem" }}>
                  <div className={styles["prices-row"]}>
                    <select
                      value={p.billing_cycle}
                      onChange={(e) =>
                        setPriceField(idx, "billing_cycle", e.target.value)
                      }
                      className={styles["plan-field-select"]}
                      title="Billing cycle"
                    >
                      {CYCLES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>

                    <InputText
                      type="number"
                      min="1"
                      value={p.billing_interval}
                      onChange={(e) =>
                        setPriceField(idx, "billing_interval", e.target.value)
                      }
                      placeholder="1"
                      title='Bills every N cycles. "1" means every month (or year). "3" with month = quarterly.'
                      className={styles["input-dark"]}
                    />

                    <InputText
                      type="number"
                      min="0"
                      step="0.01"
                      value={p.price}
                      onChange={(e) =>
                        setPriceField(idx, "price", e.target.value)
                      }
                      placeholder={
                        p.billing_cycle === "year" ? "49.99" : "4.99"
                      }
                      title="Amount charged per cycle. Use 0 for a free plan."
                      className={styles["input-dark"]}
                    />

                    <InputText
                      value={p.currency || "USD"}
                      onChange={() => {
                        /* Currency is locked to USD for v1. Multi-currency is
                           a future change (would also need per-currency Stripe
                           Prices). Field is read-only/disabled below so this
                           handler shouldn't fire — kept as a no-op safety. */
                      }}
                      disabled
                      title="Currency is fixed to USD for v1"
                      className={styles["input-dark"]}
                    />

                    <InputText
                      type="number"
                      min="0"
                      value={p.trial_days}
                      onChange={(e) =>
                        setPriceField(idx, "trial_days", e.target.value)
                      }
                      placeholder="0"
                      title="Free-trial length in days. 0 = no trial. 7 = 7-day trial then full billing."
                      className={styles["input-dark"]}
                    />

                    <label
                      className={styles["prices-row-default"]}
                      title="Preselect this price on the pricing page"
                      style={{ cursor: "pointer", fontSize: "0.8rem" }}
                    >
                      <input
                        type="radio"
                        name="default_price"
                        checked={!!p.is_default}
                        onChange={() => setPriceDefault(idx)}
                      />
                    </label>

                    <button
                      type="button"
                      className={styles["prices-row-remove"]}
                      onClick={() => removePriceRow(idx)}
                      disabled={formData.prices.length <= 1}
                      title={
                        formData.prices.length <= 1
                          ? "At least one price is required"
                          : "Remove this price"
                      }
                    >
                      ×
                    </button>
                  </div>

                  {/* Stripe Payment Link sub-row */}
                  <div style={{ marginTop: "4px" }}>
                    <InputText
                      value={p.stripe_payment_link || ""}
                      onChange={(e) =>
                        setPriceField(idx, "stripe_payment_link", e.target.value)
                      }
                      placeholder="Stripe Payment Link URL (e.g. https://buy.stripe.com/test_xxx) — leave empty if not yet set up"
                      title="Paste the Stripe Payment Link URL from your Stripe Dashboard for this price. Leave empty if Stripe isn't set up yet."
                      className={styles["input-dark"]}
                    />
                  </div>
                  </div>
                ))}
              </>
            )}
            {Object.keys(errors)
              .filter((k) => k.startsWith("price_") || k.startsWith("cycle_"))
              .map((k) => (
                <ErrText key={k} msg={errors[k]} />
              ))}
          </div>

          {/* Platform Product IDs */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <FieldLabel info="Map each price to the matching in-app purchase product/SKU on each platform's store. Leave a field empty if this price isn't sold on that platform.">
              Platform Product IDs
            </FieldLabel>
            <div className={styles["prices-editor"]}>
            {formData.prices.map((p, idx) => (
              <div key={idx} style={{ marginBottom: "0.75rem" }}>
                {formData.prices.length > 1 && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      opacity: 0.7,
                      marginBottom: "4px",
                      textTransform: "capitalize",
                    }}
                  >
                    {p.billing_cycle}
                    {p.billing_interval > 1 ? ` (every ${p.billing_interval})` : ""}
                  </div>
                )}
                <div className={styles["prices-row-platform-ids"]}>
                  <div className={styles["platform-id-field"]}>
                    <span className={styles["platform-id-field-label"]}>
                      Roku Product ID
                    </span>
                    <InputText
                      value={p.roku_product_id || ""}
                      onChange={(e) =>
                        setPriceField(idx, "roku_product_id", e.target.value)
                      }
                      placeholder="e.g. premium_monthly_roku"
                      title="Product ID for this price on Roku's in-app purchase catalog. Leave empty if not sold on Roku."
                      className={styles["input-dark"]}
                    />
                  </div>
                  <div className={styles["platform-id-field"]}>
                    <span className={styles["platform-id-field-label"]}>
                      Fire TV Product ID
                    </span>
                    <InputText
                      value={p.firetv_product_id || ""}
                      onChange={(e) =>
                        setPriceField(idx, "firetv_product_id", e.target.value)
                      }
                      placeholder="e.g. premium_monthly_firetv"
                      title="Product ID for this price on Amazon Fire TV's in-app purchase catalog. Leave empty if not sold on Fire TV."
                      className={styles["input-dark"]}
                    />
                  </div>
                  <div className={styles["platform-id-field"]}>
                    <span className={styles["platform-id-field-label"]}>
                      Android (Google Play) Product ID
                    </span>
                    <InputText
                      value={p.android_product_id || ""}
                      onChange={(e) =>
                        setPriceField(idx, "android_product_id", e.target.value)
                      }
                      placeholder="e.g. premium_monthly_gplay"
                      title="Product ID for this price on Google Play Billing. Leave empty if not sold on Android."
                      className={styles["input-dark"]}
                    />
                  </div>
                  <div className={styles["platform-id-field"]}>
                    <span className={styles["platform-id-field-label"]}>
                      tvOS / iOS Product ID
                    </span>
                    <InputText
                      value={p.tvos_product_id || ""}
                      onChange={(e) =>
                        setPriceField(idx, "tvos_product_id", e.target.value)
                      }
                      placeholder="e.g. com.vh2.premium.monthly"
                      title="Product ID for this price on Apple's App Store Connect (tvOS/iOS in-app purchase). Leave empty if not sold on Apple platforms."
                      className={styles["input-dark"]}
                    />
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* Access */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className={styles["form-group"]}>
              <FieldLabel info='How much of the catalog this plan unlocks: "limited" = some, "most" = nearly all, "full" = everything'>
                Content Access
              </FieldLabel>
              <select
                value={formData.content_access}
                onChange={(e) => setField("content_access", e.target.value)}
                className={styles["plan-field-select"]}
              >
                {ACCESS_LEVELS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel info="Max streaming quality this plan unlocks">
                Video Quality
              </FieldLabel>
              <select
                value={formData.video_quality}
                onChange={(e) => setField("video_quality", e.target.value)}
                className={styles["plan-field-select"]}
              >
                {QUALITIES.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel info="How many devices a subscriber can stream on at once">
                Max Devices
              </FieldLabel>
              <InputText
                type="number"
                min="1"
                value={formData.max_devices}
                onChange={(e) => setField("max_devices", e.target.value)}
                className={styles["input-dark"]}
              />
            </div>

            <div className={styles["form-group"]}>
              <FieldLabel info="Number of user profiles allowed per account">
                Max Profiles
              </FieldLabel>
              <InputText
                type="number"
                min="1"
                value={formData.max_profiles}
                onChange={(e) => setField("max_profiles", e.target.value)}
                className={styles["input-dark"]}
              />
            </div>
          </div>

          {/* Ad config */}
          <div className={styles["prices-editor"]}>
            <FieldLabel info="Choose which ad breaks this plan runs. Pre-roll plays before content, mid-roll during, post-roll after. Uncheck all for an ad-free plan.">
              Ad Configuration
            </FieldLabel>
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.ad_pre_roll}
                  onChange={(e) => setField("ad_pre_roll", e.target.checked)}
                />
                Pre-roll
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.ad_mid_roll}
                  onChange={(e) => setField("ad_mid_roll", e.target.checked)}
                />
                Mid-roll
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.ad_post_roll}
                  onChange={(e) => setField("ad_post_roll", e.target.checked)}
                />
                Post-roll
              </label>

            </div>
          </div>

          {/* Features */}
          <div className={styles["form-group"]}>
            <FieldLabel info="One feature per line. Each line becomes a checkmark on the pricing page.">
              Features
            </FieldLabel>
            <textarea
              name="features"
              value={formData.features}
              onChange={(e) => setField("features", e.target.value)}
              className={styles["plan-field-textarea"]}
              rows={6}
              placeholder={
                "Standard HD streaming\nWatch on 1 device\nPodcast access included"
              }
            />
          </div>

          {/* Status */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className={styles["form-group"]}>
              <FieldLabel info="Inactive plans are hidden from users but kept in the database">
                Status
              </FieldLabel>
              <select
                value={formData.status}
                onChange={(e) => setField("status", e.target.value)}
                className={styles["plan-field-select"]}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <Button
              variant="outline"
              type="button"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              {editData ? "Update Plan" : "Create Plan"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanModal;
